import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2, Bot, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const STORAGE_VISITOR = "chat_visitor";

const AIChatWidget = ({ context = "faq", propertyId = null, alwaysShow = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [available, setAvailable] = useState(true); // Default to true for visibility
  const [aiEnabled, setAiEnabled] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [visitor, setVisitor] = useState(null); // { name, email, phone }
  const [visitorForm, setVisitorForm] = useState({ name: "", email: "", phone: "" });
  const [visitorError, setVisitorError] = useState("");
  const messagesEndRef = useRef(null);

  // Restore session/visitor from localStorage on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem("chat_session_id");
    const savedMessages = localStorage.getItem("chat_messages");
    const savedVisitor = localStorage.getItem(STORAGE_VISITOR);

    if (savedSessionId) setSessionId(savedSessionId);
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.error("Error parsing saved messages:", e);
      }
    }
    if (savedVisitor) {
      try {
        setVisitor(JSON.parse(savedVisitor));
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId) localStorage.setItem("chat_session_id", sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("chat_messages", JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    // We always render the widget — backend now falls back gracefully when AI
    // is off, so the user always gets a reply and a lead is still captured.
    if (alwaysShow) {
      setAvailable(true);
      return;
    }
    const checkStatus = async () => {
      try {
        const url = propertyId
          ? `${API}/api/public/ai-chat/status?property_id=${encodeURIComponent(propertyId)}`
          : `${API}/api/public/ai-chat/status`;
        const response = await axios.get(url);
        setAvailable(response.data?.available !== false);
        setAiEnabled(!!response.data?.ai_enabled);
        if (response.data?.welcome_message) {
          setWelcomeMessage(response.data.welcome_message);
        }
      } catch (e) {
        setAvailable(true);
      }
    };
    checkStatus();
  }, [alwaysShow, propertyId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const submitVisitor = (e) => {
    e?.preventDefault();
    const name = visitorForm.name.trim();
    const email = visitorForm.email.trim();
    const phone = visitorForm.phone.trim();
    if (!name || !email) {
      setVisitorError("Por favor preencha nome e email para começar.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailOk) {
      setVisitorError("Email inválido.");
      return;
    }
    const v = { name, email, phone };
    setVisitor(v);
    localStorage.setItem(STORAGE_VISITOR, JSON.stringify(v));
    setVisitorError("");
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    if (!visitor) {
      setVisitorError("Por favor preencha os seus dados primeiro.");
      return;
    }

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/public/ai-chat`, {
        message: userMessage,
        context,
        property_id: propertyId,
        session_id: sessionId,
        visitor,
      });

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: response.data.response,
          fallback: !!response.data.fallback,
        },
      ]);
    } catch (e) {
      // Backend should not throw anymore — but if the network itself fails we
      // still surface a useful message and keep the conversation going.
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Não conseguimos contactar o servidor neste momento. A sua mensagem foi guardada — entraremos em contacto em breve.",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!available) return null;

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50"
          data-testid="ai-chat-toggle"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] h-[540px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Assistente Virtual</h3>
                <p className="text-blue-100 text-xs">
                  {aiEnabled ? "Tire as suas dúvidas" : "A nossa equipa responde-lhe em breve"}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/20 rounded-full transition"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Pre-form: collect visitor info before starting the chat */}
          {!visitor ? (
            <form
              onSubmit={submitVisitor}
              className="flex-1 overflow-y-auto p-5 bg-gray-50 flex flex-col gap-3"
              data-testid="ai-chat-prefill"
            >
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <p className="text-sm text-gray-700 mb-1 font-medium">
                  Olá! Antes de começarmos, preencha os seus dados.
                </p>
                <p className="text-xs text-gray-500">
                  Assim conseguimos dar seguimento mesmo que se desligue.
                </p>
              </div>
              <input
                type="text"
                value={visitorForm.name}
                onChange={(e) => setVisitorForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome *"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                data-testid="ai-chat-prefill-name"
              />
              <input
                type="email"
                value={visitorForm.email}
                onChange={(e) => setVisitorForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email *"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                data-testid="ai-chat-prefill-email"
              />
              <input
                type="tel"
                value={visitorForm.phone}
                onChange={(e) => setVisitorForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Telefone (opcional)"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                data-testid="ai-chat-prefill-phone"
              />
              {visitorError && (
                <p className="text-xs text-red-600">{visitorError}</p>
              )}
              <button
                type="submit"
                className="mt-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium shadow-sm transition"
                data-testid="ai-chat-prefill-submit"
              >
                Começar conversa
                <ChevronRight className="w-4 h-4" />
              </button>
              <p className="text-[11px] text-gray-400 text-center mt-1">
                Os seus dados são tratados de acordo com a nossa Política de Privacidade.
              </p>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <Bot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">Olá {visitor.name?.split(" ")[0]}! 👋</p>
                    <p className="text-sm mt-2 whitespace-pre-wrap">
                      {welcomeMessage || "Como posso ajudar?"}
                    </p>
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <div
                    key={msg.id || msg.message_id || `${msg.role}-${msg.timestamp || idx}`}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-blue-600 text-white rounded-br-md"
                          : msg.error
                          ? "bg-amber-50 text-amber-800 border border-amber-200 rounded-bl-md"
                          : "bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100 rounded-bl-md">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">A escrever...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Escreva a sua pergunta..."
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                    data-testid="ai-chat-input"
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50 transition"
                    data-testid="ai-chat-send"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
